import { isBefore } from '@repo/sdk/date'

import type { AmountFilterValue, Column, ColumnOption, DateFilterValue } from '../core'

import { resolveDateFilterValue } from './relative-date'

/*
 * One entry is kept as-is: a null amount filters by currency alone. Two entries are a range, so
 * entries without an amount are dropped, the pair is ordered by amount and shares the currencies
 * of the first. An entry with neither an amount nor a currency filters nothing.
 */
export function createAmountFilterValue(
  values: AmountFilterValue[] | undefined,
): AmountFilterValue[] {
  const entries = (values ?? []).filter(
    entry => entry.amount !== null || entry.currencies.length > 0,
  )

  if (entries.length <= 1) return entries

  const bounds = entries.filter(entry => entry.amount !== null).slice(0, 2)
  const currencies = entries[0].currencies

  if (bounds.length < 2) return [{ amount: bounds[0]?.amount ?? null, currencies }]

  return bounds
    .map(entry => ({ amount: entry.amount, currencies }))
    .sort((a, b) => (a.amount ?? 0) - (b.amount ?? 0))
}

export function createDateFilterValue(values: DateFilterValue[]) {
  if (values.length === 0) return []
  if (values.length === 1) return [values[0]]
  if (values.length === 2) return createDateRange([values[0], values[1]])
  throw new Error('Cannot create date filter value from more than 2 values')
}

// Relative values keep their rolling form; they are only resolved to decide the order.
export function createDateRange(values: [DateFilterValue, DateFilterValue]) {
  const [a, b] = values
  const [min, max] = isBefore(resolveDateFilterValue(a), resolveDateFilterValue(b))
    ? [a, b]
    : [b, a]

  return [min, max]
}

export function createNumberFilterValue(values: number[] | undefined): number[] {
  if (!values || values.length === 0) return []
  if (values.length === 1) return [values[0]]
  if (values.length === 2) return createNumberRange(values)
  return [values[0], values[1]]
}

export function createNumberRange(values: number[] | undefined) {
  let a = 0
  let b = 0

  if (!values || values.length === 0) return [a, b]
  if (values.length === 1) {
    a = values[0]
  } else {
    a = values[0]
    b = values[1]
  }

  const [min, max] = a < b ? [a, b] : [b, a]

  return [min, max]
}

export function getColumn<TData>(columns: Column<TData>[], id: string) {
  const column = columns.find(c => c.id === id)

  if (!column) {
    throw new Error(`Column with id ${id} not found`)
  }

  return column
}

export function isColumnOption(value: unknown): value is ColumnOption {
  return typeof value === 'object' && value !== null && 'value' in value && 'label' in value
}

export function isColumnOptionArray(value: unknown): value is ColumnOption[] {
  return Array.isArray(value) && value.every(isColumnOption)
}

export function isColumnOptionMap(value: unknown): value is Map<string, number> {
  if (!(value instanceof Map)) {
    return false
  }
  for (const key of value.keys()) {
    if (typeof key !== 'string') {
      return false
    }
  }
  for (const val of value.values()) {
    if (typeof val !== 'number') {
      return false
    }
  }
  return true
}

export function isMinMaxTuple(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === 'number' &&
    typeof value[1] === 'number'
  )
}

export function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(v => typeof v === 'string')
}
